import { parseMarketplaceExtensionFields } from "@/components/marketplace/contentful-fields";
import type { Package } from "@/components/marketplace/types";
import { getAssetJson, getEntry } from "@/utils/contentful";
import { isInteger, isRecord } from "@/utils/unknown";

/**
 * Packaged database schema `data_name` — the logical type name (same as `typeName` in
 * `parsePackagedSchema` in `import.ts`). Used to match existing workspace types and avoid
 * `duplicate_data_name` when importing.
 */
export function getDataName(schema: unknown): string | null {
  if (!isRecord(schema)) return null;
  const dn = schema["data_name"];
  return typeof dn === "string" && dn.trim() ? dn.trim() : null;
}

const ROOT_KINDS = new Set(["object", "agent", "automation", "task", "page"]);

/**
 * Recursively normalize a legacy (v1) package node into the current shape.
 *
 * v1 nodes carry `meta` + `content` but no `kind` discriminator — and this is
 * true not only for the root but for every embedded sub-object under
 * `content.objects` / `content.references`. v1 predates AI entities, so every
 * node is a plain graph object; we default the missing discriminator to
 * `"object"`. We also backfill an empty `content` bag when absent, since the
 * traversal (`packagedChildren`) reads `node.content` unconditionally.
 */
function migrateNode(value: unknown): unknown {
  if (!isRecord(value)) return value;

  const node: Record<string, unknown> = { ...value };
  if (typeof node.kind !== "string") node.kind = "object";

  const content = isRecord(node.content) ? { ...node.content } : {};
  for (const key of ["objects", "references"] as const) {
    const bag = content[key];
    if (isRecord(bag)) {
      const migrated: Record<string, unknown> = {};
      for (const [childKey, child] of Object.entries(bag)) {
        migrated[childKey] = migrateNode(child);
      }
      content[key] = migrated;
    }
  }
  node.content = content;
  return node;
}

/**
 * Migrate a legacy (v1) package, whose root lives under `object` and lacks a
 * `kind` discriminator, into the current shape with `root`. The whole node tree
 * is normalized recursively so embedded sub-objects are importable too.
 *
 * Returns the value unchanged if it does not look like a legacy package.
 */
export function migratePackage(value: unknown): unknown {
  if (!isRecord(value)) return value;
  if ("root" in value) return value;
  if (!("object" in value)) return value;

  const { object: legacyRoot, ...rest } = value;
  return { ...rest, root: migrateNode(legacyRoot) };
}

/** Validates the JSON shape produced by marketplace export / Contentful `content` assets. */
export function validatePackage(value: unknown): value is Package {
  if (!isRecord(value)) return false;
  const rec = value;
  if (rec.kind !== "package") return false;
  if (!isInteger(rec.version) || rec.version < 1) return false;
  if (typeof rec.createdAt !== "string") return false;
  if (typeof rec.title !== "string") return false;
  // Pre-v5 packages embedded the description and screenshot; since v5 they
  // travel only with the marketplace submission.
  if (rec.description !== undefined && typeof rec.description !== "string")
    return false;
  if (rec.screenshot !== undefined && typeof rec.screenshot !== "string")
    return false;
  const root = rec.root;
  if (!isRecord(root)) return false;
  if (typeof root.kind !== "string" || !ROOT_KINDS.has(root.kind)) return false;
  if (!isRecord(rec.databases)) return false;
  if (rec.extensions !== undefined && !isRecord(rec.extensions)) return false;
  if (rec.entities !== undefined) {
    if (!isRecord(rec.entities)) return false;
    for (const kind of ["task", "page"] as const) {
      const nodes = rec.entities[kind];
      if (nodes === undefined) continue;
      if (!Array.isArray(nodes)) return false;
      if (!nodes.every((n) => isRecord(n) && n.kind === kind)) return false;
    }
  }
  if (rec.viewConfigurations !== undefined) {
    if (!isRecord(rec.viewConfigurations)) return false;
    if (!Object.values(rec.viewConfigurations).every(isRecord)) return false;
  }
  return true;
}

/** Loads and validates a marketplace package from a Contentful extension entry id (`sys.id`). */
export async function loadPackage(
  extensionEntryId: string
): Promise<Package | null> {
  const id = extensionEntryId.trim();
  if (!id) return null;
  const entry = await getEntry(id);
  const fields = parseMarketplaceExtensionFields(entry.fields);
  const assetId = fields?.content?.sys.id;
  if (!assetId) return null;
  const json = migratePackage(await getAssetJson(assetId));
  return validatePackage(json) ? json : null;
}
